// Take the host element out of the parent's flex flow. Otherwise the
// `<rasa-conversation-feedback>` custom element sits as a flex child of
// `.messenger__content` (which is `display: flex; gap: 24px`) and reserves a
// 24px `gap` slot before itself. When the popup later unmounts, that gap
// disappears in one frame and the chat content visibly drops 24px - which
// reads as the "quick drop at the end" of the slide-down animation, even
// though the padding transition itself is perfectly linear. Making the host
// absolutely-positioned removes it from layout entirely, so unmount is a
// pure compositor operation with no layout shift.
:host {
  position: absolute;
  bottom: 80px;
  left: 0;
  right: 0;
  z-index: 10;
}

.rasa-conversation-feedback {
  position: relative;
  padding: 10px 16px 12px 16px;
  margin: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  
  // Smooth animations - slower and more elegant
  animation: fadeInUp 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  // Remove border and background when showing thank you message
  &:has(.rasa-conversation-feedback__thank-you) {
    border-top: none;
    background: transparent;
    backdrop-filter: none;
  }

      &__content {
        max-width: 100%;
        margin: 0;
        transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      }

  &__title {
    font-family: var(--widget-font-family, 'Lato', sans-serif);
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--color-primary);
    margin: 0 0 12px 0;
    text-align: center;
  }

  &__rating {
    display: flex;
    justify-content: center;
    gap: 16px;
  }

  &__thumb {
    width: 36px;
    height: 36px;
    border: 2px solid var(--feedback-thumb-border-color, #e0e0e0);
    border-radius: 50%;
    background: var(--feedback-thumb-background, #fff);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    color: #999;
    font-size: 16px;
    line-height: 1;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 0;

    svg {
      width: 20px;
      height: 20px;
      
      path {
        fill: currentColor;
      }
    }

    &:hover {
      border-color: var(--color-primary);
      background: var(--feedback-thumb-hover-bg, #f8f9fa);
      color: var(--color-primary);
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 57, 100, 0.15);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    &--positive {
      &:hover {
        border-color: var(--color-primary);
        background: var(--feedback-positive-hover-bg, #f1f8e9);
        color: var(--color-primary);
      }

      &--selected {
        border-color: var(--color-primary);
        background: var(--color-primary);
        color: white;
        box-shadow: 0 4px 12px rgba(0, 57, 100, 0.3);
      }
    }

    &--negative {
      &:hover {
        border-color: var(--color-primary);
        background: var(--feedback-negative-hover-bg, #ffebee);
        color: var(--color-primary);
      }

      &--selected {
        border-color: var(--color-primary);
        background: var(--color-primary);
        color: white;
        box-shadow: 0 4px 12px rgba(0, 57, 100, 0.3);
      }
    }
  }

      // Thank you message styling - elegant and professional.
      //
      // The fade-out is driven entirely by CSS via an animation list: first
      // `thankYouFadeIn` (0.5s, no delay), then `feedbackFadeOut` (0.6s,
      // delayed by 1.5s = THANK_YOU_HOLD_MS). `forwards` keeps the element
      // at opacity 0 once the fade completes, until the parent unmounts it.
      // Driving this from CSS removes the need for a JS setTimeout in the
      // component, which previously had to be cancelled in
      // disconnectedCallback to avoid setting state on a detached instance.
      &__thank-you {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border-radius: 0;
        animation:
          thankYouFadeIn 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94),
          feedbackFadeOut 0.6s 1.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        z-index: 20;
        pointer-events: none;
      }

      &__thank-you-content {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        padding: 0;
        background: transparent;
        border: none;
        border-radius: 0;
        animation: thankYouContentFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      }

      &__thank-you-text {
        font-family: var(--widget-font-family, 'Lato', sans-serif);
        color: var(--color-primary);
        font-weight: 600;
        font-size: 14px;
        letter-spacing: 0.2px;
        text-align: center;
        text-shadow: none;
        animation: thankYouTextPulse 1.5s ease-in-out infinite alternate;
      }
}

// Keyframe animations for smooth appearance/disappearance
@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(15px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes feedbackFadeOut {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

// Thank you message animations
@keyframes thankYouFadeIn {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes thankYouContentFade {
  0% {
    opacity: 0;
    transform: scale(0.95);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes thankYouTextPulse {
  0% {
    color: var(--color-primary);
    transform: scale(1);
  }
  100% {
    color: var(--color-primary);
    transform: scale(1.02);
  }
}

